/**
 * Slack Integration Settings - PRD-3599
 * Configure Slack workspace connection for alert notifications
 */

import React, { useState, useEffect } from 'react';
import { getSlackHealth, SlackHealthResponse } from '../../lib/slackApi';

const SlackIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
  </svg>
);

export const SlackIntegration: React.FC = () => {
  const [health, setHealth] = useState<SlackHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [defaultChannel, setDefaultChannel] = useState('#ads-alerts');
  const [criticalChannel, setCriticalChannel] = useState('#ads-critical');
  const [onCallUsers, setOnCallUsers] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHealth();
  }, []);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const result = await getSlackHealth();
      setHealth(result);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch Slack health:', err);
      setError('Could not connect to Slack API');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    // In a real implementation, this would call updateSlackSettings
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    alert('Settings saved! Note: In production, configure environment variables on the server.');
  };

  const handleTestConnection = async () => {
    alert('Test message would be sent to ' + defaultChannel + '. Configure SLACK_WEBHOOK_URL in server environment.');
  };

  const isConnected = health?.has_webhook || health?.has_bot_token;

  return (
    <div className="rounded-lg border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      {/* Header */}
      <div className="border-b border-stroke px-6 py-4 dark:border-strokedark">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#4A154B] text-white">
              <SlackIcon />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-black dark:text-white">
                Slack Integration
              </h4>
              <p className="text-sm text-body">
                Receive alert notifications in your Slack workspace
              </p>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${
            isConnected 
              ? 'bg-success/10 text-success' 
              : 'bg-warning/10 text-warning'
          }`}>
            <span className={`h-2 w-2 rounded-full ${isConnected ? 'bg-success' : 'bg-warning'}`}></span>
            {loading ? 'Checking...' : isConnected ? 'Connected' : 'Not Connected'}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5">
        {error && (
          <div className="mb-4 rounded-lg bg-danger/10 p-4 text-danger">
            {error}
          </div>
        )}

        {/* Connection Status Details */}
        {health && (
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-meta-4">
              <div className="flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${health.has_bot_token ? 'bg-success' : 'bg-gray-300'}`}></span>
                <span className="text-sm font-medium dark:text-white">Bot Token</span>
              </div>
              <p className="mt-1 text-xs text-body">
                {health.has_bot_token ? 'Configured' : 'Not set'}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-meta-4">
              <div className="flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${health.has_webhook ? 'bg-success' : 'bg-gray-300'}`}></span>
                <span className="text-sm font-medium dark:text-white">Webhook URL</span>
              </div>
              <p className="mt-1 text-xs text-body">
                {health.has_webhook ? 'Configured' : 'Not set'}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-meta-4">
              <div className="flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${health.has_signing_secret ? 'bg-success' : 'bg-gray-300'}`}></span>
                <span className="text-sm font-medium dark:text-white">Signing Secret</span>
              </div>
              <p className="mt-1 text-xs text-body">
                {health.has_signing_secret ? 'Configured' : 'Not set (optional)'}
              </p>
            </div>
          </div>
        )}

        {/* Settings Form */}
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-black dark:text-white">
              Webhook URL
            </label>
            <input
              type="url"
              placeholder="https://hooks.slack.com/services/..."
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="w-full rounded-lg border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
            />
            <p className="mt-1 text-xs text-body">
              Create an incoming webhook in your Slack workspace settings
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                Default Channel
              </label>
              <input
                type="text"
                placeholder="#ads-alerts"
                value={defaultChannel}
                onChange={(e) => setDefaultChannel(e.target.value)}
                className="w-full rounded-lg border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                Critical Alerts Channel
              </label>
              <input
                type="text"
                placeholder="#ads-critical"
                value={criticalChannel}
                onChange={(e) => setCriticalChannel(e.target.value)}
                className="w-full rounded-lg border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-black dark:text-white">
              On-Call Users (for @mentions on critical alerts)
            </label>
            <input
              type="text"
              placeholder="U123ABC, U456DEF"
              value={onCallUsers}
              onChange={(e) => setOnCallUsers(e.target.value)}
              className="w-full rounded-lg border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
            />
            <p className="mt-1 text-xs text-body">
              Comma-separated Slack user IDs. These users will be @mentioned for critical alerts.
            </p>
          </div>
        </div>

        {/* Features Info */}
        <div className="mt-6 rounded-lg bg-primary/5 p-4">
          <h5 className="mb-2 font-medium text-primary">Slack Alert Features</h5>
          <ul className="space-y-1 text-sm text-body">
            <li>✅ Rich Block Kit messages with alert details</li>
            <li>✅ Interactive buttons (Snooze 4h, Dismiss)</li>
            <li>✅ @mentions for critical alerts</li>
            <li>✅ Thread updates when alert status changes</li>
            <li>✅ Rate limiting (30/min, 100/hour per channel)</li>
          </ul>
        </div>

        {/* Example Message Preview */}
        <div className="mt-6">
          <h5 className="mb-2 text-sm font-medium text-black dark:text-white">
            Message Preview
          </h5>
          <div className="rounded-lg border border-stroke p-4 dark:border-strokedark">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-[#4A154B] text-white text-xs">
                GP
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-black dark:text-white">GP Ads AI</span>
                  <span className="text-xs text-body">Today at 3:30 PM</span>
                </div>
                <div className="mt-2 rounded bg-gray-50 p-3 dark:bg-meta-4">
                  <div className="text-lg font-semibold">🚨 Budget Burn Detected</div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div><strong>Campaign:</strong> Summer Sale 2026</div>
                    <div><strong>Platform:</strong> META</div>
                    <div><strong>Severity:</strong> CRITICAL</div>
                    <div><strong>Detected:</strong> 3:30 PM UTC</div>
                  </div>
                  <div className="mt-3 text-sm">
                    <strong>Description:</strong> Campaign spending 150% faster than planned
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button className="rounded bg-primary px-3 py-1 text-xs text-white">
                      📊 View Details
                    </button>
                    <button className="rounded border px-3 py-1 text-xs">
                      ⏰ Snooze 4h
                    </button>
                    <button className="rounded border px-3 py-1 text-xs">
                      ✓ Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-stroke px-6 py-4 dark:border-strokedark">
        <button
          onClick={handleTestConnection}
          disabled={!webhookUrl}
          className="rounded-lg border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:hover:bg-meta-4"
        >
          Test Connection
        </button>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default SlackIntegration;
